/**
 * Accounting Zustand Store
 *
 * Global state management for the Accounting tab with:
 * - Immer for immutable nested updates
 * - Persist middleware for localStorage
 * - Selection state for payments/receipts
 * - UI state (filters, dialogs)
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { AccountingFilters, AccountingSelection } from '../types';

/**
 * Store state interface
 */
export interface AccountingState {
  // Selection state
  selection: AccountingSelection;

  // Filter state
  filters: AccountingFilters;

  // UI state
  isMergeDialogOpen: boolean;
  isSplitDialogOpen: boolean;
  isManualEntryDialogOpen: boolean;
  selectedPaymentGroupId: string | null;

  // Editing state
  editingLineItemId: string | null;
  pendingChanges: Record<string, unknown>;

  // Validation state
  validationIssues: string[];
  isValidating: boolean;

  // Sync state
  isSaving: boolean;
  lastSyncedAt: Date | null;
}

/**
 * Store actions interface
 */
export interface AccountingActions {
  // Selection actions
  selectPayment: (paymentId: string) => void;
  deselectPayment: (paymentId: string) => void;
  selectAllPayments: (paymentIds: string[]) => void;
  deselectAllPayments: () => void;
  selectReceipt: (receiptId: string) => void;
  deselectReceipt: (receiptId: string) => void;
  deselectAllReceipts: () => void;
  clearSelection: () => void;

  // Filter actions
  setFilters: (filters: Partial<AccountingFilters>) => void;
  clearFilters: () => void;

  // UI actions
  openMergeDialog: () => void;
  closeMergeDialog: () => void;
  openSplitDialog: (paymentGroupId: string) => void;
  closeSplitDialog: () => void;
  openManualEntryDialog: () => void;
  closeManualEntryDialog: () => void;

  // Editing actions
  startEditing: (lineItemId: string) => void;
  stopEditing: () => void;
  setPendingChange: (field: string, value: unknown) => void;
  clearPendingChanges: () => void;

  // Validation actions
  setValidationIssues: (issues: string[]) => void;
  clearValidationIssues: () => void;
  setValidating: (isValidating: boolean) => void;

  // Sync actions
  setSaving: (isSaving: boolean) => void;
  setLastSyncedAt: (date: Date) => void;
}

/**
 * Combined store type
 */
export type AccountingStore = AccountingState & AccountingActions;

/**
 * Initial state
 */
const initialState: AccountingState = {
  selection: {
    selectedPaymentIds: [],
    selectedReceiptIds: [],
  },
  filters: {
    showOnlyPending: false,
    showOnlyManual: false,
  },
  isMergeDialogOpen: false,
  isSplitDialogOpen: false,
  isManualEntryDialogOpen: false,
  selectedPaymentGroupId: null,
  editingLineItemId: null,
  pendingChanges: {},
  validationIssues: [],
  isValidating: false,
  isSaving: false,
  lastSyncedAt: null,
};

/**
 * Create the Zustand store with persist and immer middleware
 */
export const useAccountingStore = create<AccountingStore>()(
  persist(
    immer((set) => ({
      ...initialState,

      // Selection actions
      selectPayment: (paymentId) =>
        set((state) => {
          if (!state.selection.selectedPaymentIds.includes(paymentId)) {
            state.selection.selectedPaymentIds.push(paymentId);
          }
        }),

      deselectPayment: (paymentId) =>
        set((state) => {
          state.selection.selectedPaymentIds = state.selection.selectedPaymentIds.filter(
            (id) => id !== paymentId,
          );
        }),

      selectAllPayments: (paymentIds) =>
        set((state) => {
          state.selection.selectedPaymentIds = [...paymentIds];
        }),

      deselectAllPayments: () =>
        set((state) => {
          state.selection.selectedPaymentIds = [];
        }),

      selectReceipt: (receiptId) =>
        set((state) => {
          if (!state.selection.selectedReceiptIds.includes(receiptId)) {
            state.selection.selectedReceiptIds.push(receiptId);
          }
        }),

      deselectReceipt: (receiptId) =>
        set((state) => {
          state.selection.selectedReceiptIds = state.selection.selectedReceiptIds.filter(
            (id) => id !== receiptId,
          );
        }),

      deselectAllReceipts: () =>
        set((state) => {
          state.selection.selectedReceiptIds = [];
        }),

      clearSelection: () =>
        set((state) => {
          state.selection.selectedPaymentIds = [];
          state.selection.selectedReceiptIds = [];
        }),

      // Filter actions
      setFilters: (newFilters) =>
        set((state) => {
          state.filters = { ...state.filters, ...newFilters };
        }),

      clearFilters: () =>
        set((state) => {
          state.filters = {
            showOnlyPending: false,
            showOnlyManual: false,
          };
        }),

      // UI actions
      openMergeDialog: () =>
        set((state) => {
          state.isMergeDialogOpen = true;
        }),

      closeMergeDialog: () =>
        set((state) => {
          state.isMergeDialogOpen = false;
        }),

      openSplitDialog: (paymentGroupId) =>
        set((state) => {
          state.isSplitDialogOpen = true;
          state.selectedPaymentGroupId = paymentGroupId;
        }),

      closeSplitDialog: () =>
        set((state) => {
          state.isSplitDialogOpen = false;
          state.selectedPaymentGroupId = null;
        }),

      openManualEntryDialog: () =>
        set((state) => {
          state.isManualEntryDialogOpen = true;
        }),

      closeManualEntryDialog: () =>
        set((state) => {
          state.isManualEntryDialogOpen = false;
        }),

      // Editing actions
      startEditing: (lineItemId) =>
        set((state) => {
          state.editingLineItemId = lineItemId;
          state.pendingChanges = {};
        }),

      stopEditing: () =>
        set((state) => {
          state.editingLineItemId = null;
          state.pendingChanges = {};
        }),

      setPendingChange: (field, value) =>
        set((state) => {
          state.pendingChanges[field] = value;
        }),

      clearPendingChanges: () =>
        set((state) => {
          state.pendingChanges = {};
        }),

      // Validation actions
      setValidationIssues: (issues) =>
        set((state) => {
          state.validationIssues = issues;
        }),

      clearValidationIssues: () =>
        set((state) => {
          state.validationIssues = [];
        }),

      setValidating: (isValidating) =>
        set((state) => {
          state.isValidating = isValidating;
        }),

      // Sync actions
      setSaving: (isSaving) =>
        set((state) => {
          state.isSaving = isSaving;
        }),

      setLastSyncedAt: (date) =>
        set((state) => {
          state.lastSyncedAt = date;
        }),
    })),
    {
      name: 'accounting-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        filters: state.filters,
      }),
    },
  ),
);

/**
 * Selectors for computed/derived values
 */

// Select selection state
export const selectSelectedPaymentIds = (state: AccountingStore): string[] =>
  state.selection.selectedPaymentIds;
export const selectSelectedReceiptIds = (state: AccountingStore): string[] =>
  state.selection.selectedReceiptIds;
export const selectHasSelection = (state: AccountingStore): boolean =>
  state.selection.selectedPaymentIds.length > 0 || state.selection.selectedReceiptIds.length > 0;

// Select filter state
export const selectFilters = (state: AccountingStore): AccountingFilters => state.filters;

// Select UI state
export const selectIsMergeDialogOpen = (state: AccountingStore): boolean => state.isMergeDialogOpen;
export const selectIsSplitDialogOpen = (state: AccountingStore): boolean => state.isSplitDialogOpen;
export const selectIsManualEntryDialogOpen = (state: AccountingStore): boolean =>
  state.isManualEntryDialogOpen;

// Select editing state
export const selectEditingLineItemId = (state: AccountingStore): string | null =>
  state.editingLineItemId;
export const selectPendingChanges = (state: AccountingStore): Record<string, unknown> =>
  state.pendingChanges;
export const selectIsEditing = (state: AccountingStore): boolean =>
  state.editingLineItemId !== null;

// Select validation state
export const selectValidationIssues = (state: AccountingStore): string[] => state.validationIssues;
export const selectIsValidating = (state: AccountingStore): boolean => state.isValidating;
export const selectHasValidationIssues = (state: AccountingStore): boolean =>
  state.validationIssues.length > 0;

// Select sync state
export const selectIsSaving = (state: AccountingStore): boolean => state.isSaving;
export const selectLastSyncedAt = (state: AccountingStore): Date | null => state.lastSyncedAt;
